import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  useTicketSystems,
  useTicketFunctions,
  useTicketDetail,
  useCreateTicket,
  useUpdateTicket,
  useSubmitTicket,
  useUploadTicketImage,
  useDeleteTicketImage,
} from '@saiens/shared/hooks';
import type { TicketType, TicketPriority } from '@saiens/shared/types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft, Upload, X, Send, Save } from 'lucide-react';

const PRIORITY_OPTIONS: { value: TicketPriority; label: string }[] = [
  { value: 'low', label: '低' },
  { value: 'medium', label: '中' },
  { value: 'high', label: '高' },
  { value: 'urgent', label: '緊急' },
];

export function TicketFormPage() {
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const numericId = id ? parseInt(id, 10) : undefined;
  const typeFromUrl = (searchParams.get('type') as TicketType) || 'requirement';
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Data fetching
  const { data: ticket, isLoading: ticketLoading } = useTicketDetail(numericId);
  const { data: systems } = useTicketSystems();

  // Form state
  const [ticketType, setTicketType] = useState<TicketType>(typeFromUrl);
  const [systemId, setSystemId] = useState<number | undefined>();
  const [functionId, setFunctionId] = useState<number | undefined>();
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [expectedDate, setExpectedDate] = useState('');
  const [description, setDescription] = useState('');

  const { data: functions } = useTicketFunctions(systemId);

  // Mutations
  const createMutation = useCreateTicket();
  const updateMutation = useUpdateTicket();
  const submitMutation = useSubmitTicket();
  const uploadMutation = useUploadTicketImage();
  const deleteImageMutation = useDeleteTicketImage();

  // Init form from loaded data
  useEffect(() => {
    if (ticket && isEdit) {
      setTicketType(ticket.ticket_type);
      setSystemId(ticket.system_id ?? undefined);
      setFunctionId(ticket.function_id ?? undefined);
      setPriority(ticket.priority);
      setExpectedDate(ticket.expected_date ?? '');
      setDescription(ticket.description ?? '');
    }
  }, [ticket, isEdit]);

  // Guard: redirect if editing a non-draft ticket
  useEffect(() => {
    if (ticket && isEdit && ticket.state !== 'draft') {
      navigate(`/ticket/${numericId}`, { replace: true });
    }
  }, [ticket, isEdit, numericId, navigate]);

  const handleSystemChange = useCallback((value: string) => {
    const newSystemId = value ? parseInt(value, 10) : undefined;
    setSystemId(newSystemId);
    setFunctionId(undefined);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      const data = {
        system_id: systemId || null,
        function_id: functionId || null,
        priority,
        expected_date: expectedDate || null,
        description,
      };

      if (isEdit && numericId) {
        await updateMutation.mutateAsync({ id: numericId, data });
      } else {
        const result = await createMutation.mutateAsync({
          ticket_type: ticketType,
          ...data,
          system_id: systemId,
          function_id: functionId,
          expected_date: expectedDate || undefined,
        });
        navigate(`/ticket/${result.id}/edit`, { replace: true });
      }
    } catch {
      // Error handled by mutation onError
    }
  }, [
    isEdit, numericId, ticketType, systemId, functionId,
    priority, expectedDate, description,
    createMutation, updateMutation, navigate,
  ]);

  const handleSubmit = useCallback(async () => {
    try {
      if (isEdit && numericId) {
        await updateMutation.mutateAsync({
          id: numericId,
          data: {
            system_id: systemId || null,
            function_id: functionId || null,
            priority,
            expected_date: expectedDate || null,
            description,
          },
        });
        await submitMutation.mutateAsync(numericId);
        navigate(`/ticket/${numericId}`, { replace: true });
      } else {
        const result = await createMutation.mutateAsync({
          ticket_type: ticketType,
          system_id: systemId,
          function_id: functionId,
          priority,
          expected_date: expectedDate || undefined,
          description,
        });
        await submitMutation.mutateAsync(result.id);
        navigate(`/ticket/${result.id}`, { replace: true });
      }
    } catch {
      // Error handled by mutation onError
    }
  }, [
    isEdit, numericId, ticketType, systemId, functionId,
    priority, expectedDate, description,
    createMutation, updateMutation, submitMutation, navigate,
  ]);

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || !numericId) return;

      for (let i = 0; i < files.length; i++) {
        try {
          await uploadMutation.mutateAsync({ ticketId: numericId, file: files[i] });
        } catch {
          // Error handled by mutation onError
        }
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [numericId, uploadMutation],
  );

  const handleDeleteAttachment = useCallback(
    async (attachmentId: number) => {
      if (!numericId) return;
      try {
        await deleteImageMutation.mutateAsync({ ticketId: numericId, attachmentId });
      } catch {
        // Error handled by mutation onError
      }
    },
    [numericId, deleteImageMutation],
  );

  const isPending =
    createMutation.isPending || updateMutation.isPending || submitMutation.isPending;

  if (isEdit && ticketLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const title = isEdit
    ? `編輯 ${ticket?.name || ''}`
    : ticketType === 'requirement'
      ? '新增系統需求'
      : '新增 BUG 回報';

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Header */}
      <div className="flex items-center gap-3 bg-white px-4 py-3 shadow-sm">
        <button type="button" onClick={() => navigate(-1)} className="text-gray-600">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-bold text-gray-900">{title}</h1>
      </div>

      {/* Form */}
      <div className="flex-1 space-y-4 p-4 pb-32">
        {/* Type (only for create) */}
        {!isEdit && (
          <FormSection label="工單類型">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setTicketType('requirement')}
                className={`flex-1 rounded-lg border py-2 text-sm font-medium transition-colors ${
                  ticketType === 'requirement'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                系統需求
              </button>
              <button
                type="button"
                onClick={() => setTicketType('bug')}
                className={`flex-1 rounded-lg border py-2 text-sm font-medium transition-colors ${
                  ticketType === 'bug'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                BUG 回報
              </button>
            </div>
          </FormSection>
        )}

        {/* System */}
        <FormSection label="系統別">
          <select
            value={systemId ?? ''}
            onChange={(e) => handleSystemChange(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
          >
            <option value="">請選擇系統</option>
            {systems?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </FormSection>

        {/* Function */}
        <FormSection label="功能名稱">
          <select
            value={functionId ?? ''}
            onChange={(e) => setFunctionId(e.target.value ? parseInt(e.target.value, 10) : undefined)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
            disabled={!systemId}
          >
            <option value="">請選擇功能</option>
            {functions?.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </FormSection>

        {/* Priority */}
        <FormSection label="優先級">
          <div className="flex gap-2">
            {PRIORITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPriority(opt.value)}
                className={`flex-1 rounded-lg border py-2 text-sm font-medium transition-colors ${
                  priority === opt.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </FormSection>

        {/* Expected date */}
        <FormSection label="期望完成日">
          <input
            type="date"
            value={expectedDate}
            onChange={(e) => setExpectedDate(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
          />
        </FormSection>

        {/* Description */}
        <FormSection label="描述">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              ticketType === 'requirement'
                ? '請描述您的需求...'
                : '請描述 BUG 情況（重現步驟、預期結果、實際結果）...'
            }
            rows={6}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
          />
        </FormSection>

        {/* Attachments (edit mode only) */}
        {isEdit && ticket && (
          <FormSection label="附件">
            {ticket.attachments.length > 0 && (
              <div className="mb-3 space-y-2">
                {ticket.attachments.map((att) => (
                  <div
                    key={att.id}
                    className="flex items-center justify-between rounded-lg border border-gray-200 p-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-gray-700">{att.name}</p>
                      <p className="text-xs text-gray-400">
                        {(att.file_size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteAttachment(att.id)}
                      disabled={deleteImageMutation.isPending}
                      className="ml-2 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500 disabled:opacity-50"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500 hover:border-primary hover:text-primary"
            >
              <Upload className="h-4 w-4" />
              {uploadMutation.isPending ? '上傳中...' : '上傳附件'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              onChange={handleFileUpload}
              className="hidden"
            />
          </FormSection>
        )}
      </div>

      {/* Footer actions */}
      <div className="safe-area-bottom fixed bottom-16 left-0 right-0 border-t border-gray-200 bg-white px-4 py-3">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={isPending}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 py-3 text-sm font-medium text-gray-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {isPending ? '處理中...' : '儲存草稿'}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isPending}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-medium text-white disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {isPending ? '處理中...' : '直接送出'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl bg-white p-4 shadow-sm">
      <label className="mb-2 block text-sm font-medium text-gray-700">{label}</label>
      {children}
    </div>
  );
}
